import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BoltIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  TrophyIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { getPublicBootcamp } from '../../api/bootcampApi';
import Spinner from '../../components/ui/Spinner';

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function ScoreBadge({ score }) {
  if (score == null) return <span className="text-xs text-neutral-400">—</span>;
  const s = Math.round(score);
  const color =
    s >= 80 ? 'bg-emerald-100 text-emerald-700' :
    s >= 60 ? 'bg-blue-100 text-blue-700' :
    s >= 40 ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${color}`}>
      {s}
    </span>
  );
}

const STATUS_STYLES = {
  active:    'bg-emerald-100 text-emerald-700 ring-emerald-200',
  completed: 'bg-blue-100 text-blue-700 ring-blue-200',
  draft:     'bg-amber-100 text-amber-700 ring-amber-200',
};

// ── Page ─────────────────────────────────────────────────────────────────────
export default function PublicBootcampDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [bootcamp, setBootcamp] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    getPublicBootcamp(id)
      .then((res) => setBootcamp(res.data?.data ?? res.data))
      .catch(() => setError('Bootcamp not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-primary-50">
        <Spinner size="xl" className="text-primary-600" />
      </div>
    );
  }

  if (error || !bootcamp) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-br from-slate-50 via-white to-primary-50">
        <p className="text-sm text-red-500">{error ?? 'Not found'}</p>
        <button onClick={() => navigate('/')} className="text-sm text-primary-600 underline">
          Back to home
        </button>
      </div>
    );
  }

  const participants  = bootcamp.participants ?? [];
  const analyzed      = participants.filter((p) => p.overall_score != null);
  const avgScore      = analyzed.length
    ? Math.round(analyzed.reduce((s, p) => s + parseFloat(p.overall_score), 0) / analyzed.length)
    : null;
  const statusStyle   = STATUS_STYLES[bootcamp.status] ?? STATUS_STYLES.draft;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50 font-sans">

      {/* ── Navbar ── */}
      <header className="sticky top-0 z-30 border-b border-white/80 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            All Bootcamps
          </button>
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-600">
              <BoltIcon className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold font-heading text-neutral-900 tracking-tight">
              FounderMind <span className="text-primary-600">AI</span>
            </span>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
          >
            Login <ArrowRightIcon className="h-3 w-3" />
          </button>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-primary-700 via-primary-600 to-secondary-700 px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 capitalize ${statusStyle}`}>
              {bootcamp.status}
            </span>
            {bootcamp.organization_name && (
              <span className="text-xs text-primary-200">{bootcamp.organization_name}</span>
            )}
          </div>

          <h1 className="text-3xl font-extrabold font-heading text-white sm:text-4xl">
            {bootcamp.title}
          </h1>
          {bootcamp.cohort_name && (
            <p className="mt-1 text-lg text-primary-200">{bootcamp.cohort_name}</p>
          )}
          {bootcamp.description && (
            <p className="mt-4 max-w-2xl text-sm text-primary-100 leading-relaxed">
              {bootcamp.description}
            </p>
          )}

          {/* Date */}
          {(bootcamp.start_date || bootcamp.end_date) && (
            <div className="mt-5 flex items-center gap-2 text-sm text-primary-200">
              <CalendarDaysIcon className="h-4 w-4 shrink-0" />
              <span>
                {fmtDate(bootcamp.start_date) ?? '?'} — {fmtDate(bootcamp.end_date) ?? 'Ongoing'}
              </span>
            </div>
          )}

          {/* Stats row */}
          <div className="mt-8 flex flex-wrap gap-6">
            <div className="flex flex-col">
              <span className="text-2xl font-extrabold text-white">{participants.length}</span>
              <span className="text-xs text-primary-300">Participants</span>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-extrabold text-white">{bootcamp.assessment_count ?? 0}</span>
              <span className="text-xs text-primary-300">Assessments</span>
            </div>
            {avgScore != null && (
              <div className="flex flex-col">
                <span className="text-2xl font-extrabold text-white">{avgScore}</span>
                <span className="text-xs text-primary-300">Avg AI Score</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Participants ── */}
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-3">
          <UserGroupIcon className="h-5 w-5 text-primary-600" />
          <h2 className="text-xl font-bold font-heading text-neutral-900">
            Participants ({participants.length})
          </h2>
        </div>

        {participants.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-neutral-200 bg-white py-16 text-center">
            <UserGroupIcon className="h-12 w-12 text-neutral-300" />
            <div>
              <p className="text-sm font-medium text-neutral-600">No submissions yet</p>
              <p className="mt-1 text-xs text-neutral-400">
                Participants will appear here once they complete an assessment.
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
            {/* Table header */}
            <div className="hidden grid-cols-[1fr_1fr_1fr_auto] gap-4 border-b border-neutral-100 bg-neutral-50 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 sm:grid">
              <span>Participant</span>
              <span>Assessment</span>
              <span>Submitted</span>
              <span className="text-right">AI Score</span>
            </div>

            <ul className="divide-y divide-neutral-100">
              {participants.map((p, i) => (
                <li
                  key={i}
                  className="grid grid-cols-1 gap-2 px-6 py-4 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-center sm:gap-4"
                >
                  {/* Name + startup */}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900">{p.full_name}</p>
                    {p.startup_name && (
                      <p className="truncate text-xs text-neutral-500">{p.startup_name}</p>
                    )}
                  </div>

                  {/* Assessment */}
                  <div className="min-w-0">
                    <p className="truncate text-sm text-neutral-700">{p.assessment_title}</p>
                  </div>

                  {/* Date */}
                  <div>
                    <p className="text-sm text-neutral-500">
                      {p.submitted_at ? fmtDate(p.submitted_at) : '—'}
                    </p>
                  </div>

                  {/* Score */}
                  <div className="flex items-center justify-start sm:justify-end">
                    {p.overall_score != null ? (
                      <div className="flex items-center gap-1.5">
                        <TrophyIcon className="h-3.5 w-3.5 text-amber-400" />
                        <ScoreBadge score={p.overall_score} />
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400">Not analyzed</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-6 text-sm text-neutral-400 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-600">
              <BoltIcon className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-semibold text-neutral-600 font-heading">FounderMind AI</span>
          </div>
          <p>© {new Date().getFullYear()} FounderMind AI</p>
        </div>
      </footer>
    </div>
  );
}
